import React, { useState } from 'react';
import { Search, Filter, LayoutGrid, List, ArrowUpDown, ChevronDown } from 'lucide-react';

interface ClientFiltersProps {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  segmentFilter: string;
  setSegmentFilter: (v: string) => void;
  viewMode: 'table' | 'grid';
  setViewMode: (v: 'table' | 'grid') => void;
  sortField: string;
  setSortField: (v: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (v: 'asc' | 'desc') => void;
}

export default function ClientFilters({
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  segmentFilter, setSegmentFilter,
  viewMode, setViewMode,
  sortField, setSortField,
  sortOrder, setSortOrder
}: ClientFiltersProps) {
  const [isSortOpen, setIsSortOpen] = useState(false);

  const tabs = [
    { id: 'ALL', label: 'Todos' },
    { id: 'CLIENTES', label: 'Clientes' },
    { id: 'LEADS', label: 'Leads' },
    { id: 'VIP', label: 'VIP' },
    { id: 'NEGOCIACAO', label: 'Em negociação' },
    { id: 'INACTIVE', label: 'Inativos' },
  ];

  const sortOptions = [
    { field: 'name', label: 'Nome' },
    { field: 'lastContactDays', label: 'Último contato' },
    { field: 'status', label: 'Status' },
    { field: 'projects', label: 'Projetos' },
    { field: 'responsibleName', label: 'Responsável' },
  ];

  const handleSortSelect = (field: string) => {
    if (sortField === field) {
      // Toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setIsSortOpen(false);
  };

  const currentSortLabel = sortOptions.find(o => o.field === sortField)?.label || 'Ordenar';

  return (
    <div className="flex flex-col gap-4">
      {/* Tab bar for Status Filter (Todos, Clientes, Leads, VIP, Em negociação, Inativos) */}
      <div className="overflow-x-auto scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-2 p-1.5 bg-slate-100/60 border border-slate-200/40 rounded-2xl w-max sm:w-full">
          {tabs.map((tab) => {
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap flex-1 text-center ${
                  isActive
                    ? 'bg-white text-indigo-600 shadow-sm border border-[#0F172A05]'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50/50'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main filter, search and sort controls row */}
      <div className="flex flex-col lg:flex-row items-stretch sm:items-center justify-between gap-3.5 bg-white border border-[#0F172A0F] p-3.5 rounded-[24px] shadow-[0_1px_3px_rgba(15,23,42,0.03)]">
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full lg:w-auto flex-1">
          {/* Search bar */}
          <div className="relative w-full sm:max-w-[340px]">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Pesquisar por nome, empresa ou email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs font-medium border border-slate-200/80 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 transition-all bg-slate-50/50 hover:bg-slate-50 placeholder:text-slate-400"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            {/* Segment filter */}
            <div className="flex items-center gap-2 flex-1 sm:flex-none justify-between sm:justify-start">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Segmento:</span>
              <select 
                value={segmentFilter}
                onChange={(e) => setSegmentFilter(e.target.value)}
                className="text-xs font-semibold text-slate-700 bg-slate-50/80 border border-slate-200/80 px-3 py-1.5 rounded-xl focus:outline-none focus:border-indigo-500 hover:border-slate-300 transition-colors cursor-pointer"
              >
                <option value="ALL">Todos Segmentos</option>
                <option value="TECNOLOGIA">Tecnologia</option>
                <option value="FINANCEIRO">Financeiro</option>
                <option value="SAUDE">Saúde</option>
                <option value="VAREJO">Varejo</option>
                <option value="SERVIÇOS">Serviços</option>
                <option value="LOGÍSTICA">Logística</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 w-full lg:w-auto justify-between lg:justify-end">
          {/* Dropdown for sorting */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setIsSortOpen(!isSortOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200/80 text-slate-700 font-bold text-xs hover:bg-slate-100 transition-all cursor-pointer shadow-3xs"
            >
              <ArrowUpDown size={14} className="text-slate-400" />
              <span>{currentSortLabel} ({sortOrder === 'asc' ? 'A-Z' : 'Z-A'})</span>
              <ChevronDown size={13} className="text-slate-400" />
            </button>

            {isSortOpen && (
              <div className="absolute right-0 mt-1.5 w-48 bg-white border border-slate-100 rounded-xl shadow-lg z-50 py-1 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.field}
                    onClick={() => handleSortSelect(opt.field)}
                    className={`w-full px-4 py-2 text-left text-xs font-semibold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                      sortField === opt.field ? 'text-indigo-600 bg-indigo-50/40' : 'text-slate-600'
                    }`}
                  >
                    <span>{opt.label}</span>
                    {sortField === opt.field && (
                      <span className="text-[9px] font-bold bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded uppercase">
                        {sortOrder === 'asc' ? 'Cresc.' : 'Decresc.'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View Mode (Table/Grid Toggle) */}
          <div className="flex items-center bg-slate-100/70 border border-slate-200/60 rounded-xl p-0.5">
            <button 
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-lg transition-all cursor-pointer ${viewMode === 'table' ? 'bg-white shadow-3xs text-indigo-600 font-bold' : 'text-slate-400 hover:text-slate-600'}`}
              title="Visualização em Lista"
            >
              <List size={14} />
            </button>
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white shadow-3xs text-indigo-600 font-bold' : 'text-slate-400 hover:text-slate-600'}`}
              title="Visualização em Grade"
            >
              <LayoutGrid size={14} />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
